import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import { fetchTaskById } from 'api/tasks';
import * as Form from 'components/form';
import { TASK_LINK_DATA_ATTRIBUTE } from 'components/tasks/constants';
import { TaskFormLinkedResource } from 'components/tasks/types';
import { generateTaggedResourceHtml } from 'components/tasks/utils/generateTaggedResourceHtml';
import { useCrmMembers } from 'components/tasks/utils/useCrmMembers';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { dateFromServer } from 'helpers/dates';
import { getNodesFromHtml } from 'helpers/dom';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { queryClient } from 'queries/queryClient';
import { Campaign, Contact, TaskStatus } from 'types/domain';

interface TaskStatusRecord {
    id: TaskStatus;
    label: string;
}
interface TaskFormValues {
    status: TaskStatus;
    title: string;
    description: string;
    assigned_to: string[];
    due_at: DateTime | null;
    remind_at: DateTime | null;
    links: TaskFormLinkedResource[];
}

const taskStatuses: TaskStatusRecord[] = [
    {
        id: 'todo',
        label: '📝 To do',
    },
    {
        id: 'in_progress',
        label: '🚧 In progress',
    },
    {
        id: 'complete',
        label: '✅ Complete',
    },
];

const useTaskForm = ({
    taskId,
    defaultStatus,
    defaultAssigneeId,
}: {
    taskId?: number | null;
    defaultStatus?: TaskStatus;
    defaultAssigneeId?: number;
}) => {
    const location = useLocation();
    const { accountId } = useCurrentAccount();
    const isEditing = !!taskId;
    const id = taskId ?? -1;
    const { data: task } = useSuspenseQuery({
        queryKey: accountQueryKeys.taskById(accountId, id),
        queryFn: () => conditionallyFetchOrNull(() => fetchTaskById(accountId, id), isEditing),
    });

    const { data: members } = useCrmMembers();

    const status = useMemo(() => {
        if (task) return task.status;
        if (defaultStatus) return defaultStatus;
        return 'todo';
    }, [defaultStatus, task]);

    const links = useMemo(() => {
        if (task) {
            const linksWithLinkables = task.links.filter(l => !!l.linkable);
            return linksWithLinkables.map(l => {
                return {
                    linkable_type: l.linkable_type,
                    linkable_id: l.linkable?.id,
                    value: l.linkable,
                } as TaskFormLinkedResource;
            });
        }

        const foundContactId = /contacts\/(\d+)/g.exec(location.pathname);
        const foundCampaignId = /campaigns\/(\d+)/g.exec(location.pathname);

        if (foundContactId) {
            const contact: Contact | undefined = queryClient.getQueryData(
                accountQueryKeys.contactById(accountId, parseInt(foundContactId[1])),
            );
            if (contact) {
                const linkedContact: TaskFormLinkedResource = {
                    linkable_id: contact.id,
                    linkable_type: 'contact',
                    value: contact,
                };
                return [linkedContact];
            }
        }

        if (foundCampaignId) {
            const campaign: Campaign | undefined = queryClient.getQueryData(
                campaignQueryKeys.campaign(parseInt(foundCampaignId[1])),
            );
            if (campaign) {
                const linkedCampaign: TaskFormLinkedResource = {
                    linkable_id: campaign.id,
                    linkable_type: 'campaign',
                    value: campaign,
                };
                return [linkedCampaign];
            }
        }

        return [];
    }, [task, location, accountId]);

    const hydratedDescription = useMemo(() => {
        if (task) {
            if (!task.description || task.links.length === 0) {
                return task.description;
            }

            const body = getNodesFromHtml({ html: task.description, selectors: 'body' })[0];
            const descriptionLinks = body.querySelectorAll(`span[${TASK_LINK_DATA_ATTRIBUTE}]`);

            for (const link of Array.from(descriptionLinks)) {
                const linkId = (link as HTMLElement).dataset.gbLinkId;
                const linkType = (link as HTMLElement).dataset.gbLinkType;
                const foundLink = task.links.find(
                    l => l.linkable_type === linkType && l.linkable?.id.toString() === linkId,
                );

                if (!linkId || !linkType || !foundLink?.linkable) continue;

                link.outerHTML = generateTaggedResourceHtml({
                    resource: foundLink.linkable,
                    type: foundLink.linkable_type,
                });
            }
            return body.innerHTML;
        } else if (links.length > 0) {
            return `<p>${generateTaggedResourceHtml({
                resource: links[0].value,
                type: links[0].linkable_type,
            })}&nbsp;</p>`;
        }
    }, [task, links]);

    const defaultAssignee = useMemo(() => {
        if (defaultAssigneeId) {
            return [defaultAssigneeId.toString()];
        }

        const foundMember = members.find(m => m.id === task?.assigned_to);
        if (foundMember) {
            return [foundMember.id.toString()];
        }

        return [];
    }, [defaultAssigneeId, members, task?.assigned_to]);

    const methods = Form.useGivebutterForm<TaskFormValues>({
        mode: 'all',
        defaultValues: {
            status: status,
            title: task?.title ?? '',
            description: hydratedDescription ?? '',
            assigned_to: defaultAssignee,
            due_at: task?.due_at ? dateFromServer(task.due_at) : undefined,
            remind_at: task?.remind_at ? dateFromServer(task.remind_at) : undefined,
            links,
        },
    });

    return {
        methods,
    };
};

export { useTaskForm, taskStatuses };
export type { TaskFormValues };
